'use client';

import { useEffect, useRef, useState } from 'react';
import { catalogCdnBaseUrl } from '../lib/config/cdn';
import { SpineEngine } from '../lib/live2d/spine-engine';
import type { SpineAsset } from '../lib/heroes/types';

type Props = {
  asset?: SpineAsset;
  fallback: string;
  title: string;
  className: string;
  animation?: string;
  eager?: boolean;
  /** When set, overrides intersection-based playback (banner expand/collapse). */
  playing?: boolean;
  onAnimations?: (animations: string[]) => void;
};

/**
 * Live2D / Spine host. Runtimes (1.6 / 2.0 / 3.7 / 3.8 / 4.x) load by
 * asset.runtime inside the component — no iframe.
 */
export default function SpinePlayer({
  asset,
  fallback,
  title,
  className,
  animation = '',
  eager = false,
  playing,
  onAnimations,
}: Props) {
  if (asset && className === 'spine-portrait' && asset.scale === undefined) {
    asset = { ...asset, framing: asset.framing ?? {} };
  }
  // Imported battle units share Elaine's source-space reference height. Preserve
  // hand-tuned assets and never apply battle-unit sizing to cover portraits.
  if (asset && className === 'v2-sd-stage' && asset.scale === undefined) {
    asset = { ...asset, scale: 0.76, referenceHeight: 738.32 };
  }
  const assetKey = asset ? JSON.stringify(asset) : 'static';
  const [displayed, setDisplayed] = useState<{ key: string; props: Props } | null>(
    null,
  );
  const [retiring, setRetiring] = useState<{ key: string; props: Props } | null>(
    null,
  );
  const retireTimer = useRef<number | null>(null);
  const incoming: Props = {
    asset,
    fallback,
    title,
    className: 'hero-spine-layer',
    animation,
    eager,
    playing,
  };
  const previous =
    asset && displayed && displayed.key !== assetKey ? displayed : null;

  useEffect(
    () => () => {
      if (retireTimer.current !== null) window.clearTimeout(retireTimer.current);
    },
    [],
  );

  const finishSwitch = () => {
    if (displayed && displayed.key !== assetKey) {
      setRetiring(displayed);
      if (retireTimer.current !== null) window.clearTimeout(retireTimer.current);
      retireTimer.current = window.setTimeout(() => {
        setRetiring(null);
        retireTimer.current = null;
      }, 220);
    }
    setDisplayed({ key: assetKey, props: incoming });
  };

  // A rapid second click should retain the currently displayed form, not an
  // older layer that may still be finishing its fade-out.
  const outgoing = previous ?? retiring;

  return (
    <div className={`${className} hero-spine-host`}>
      {outgoing && (
        <PlayerStage
          key={outgoing.key}
          {...outgoing.props}
          className={`hero-spine-layer${retiring?.key === outgoing.key ? ' is-retiring' : ''}`}
        />
      )}
      <PlayerStage
        key={assetKey}
        {...incoming}
        hasPrevious={!!previous}
        onReady={finishSwitch}
        onAnimations={onAnimations}
      />
    </div>
  );
}

function PlayerStage({
  asset,
  fallback,
  title,
  className,
  animation = '',
  eager = false,
  playing,
  onAnimations,
  hasPrevious = false,
  onReady,
}: Props & { hasPrevious?: boolean; onReady?: () => void }) {
  const host = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const engineRef = useRef<SpineEngine | null>(null);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(eager);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);
  const activePlayback = playing === undefined ? visible : playing;
  const latest = useRef({
    animation,
    activePlayback,
    asset,
    onReady,
    onAnimations,
  });
  latest.current = { animation, activePlayback, asset, onReady, onAnimations };

  useEffect(() => {
    if (eager) setMounted(true);
    const node = host.current;
    if (!node) return;
    const preload = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMounted(true);
          preload.disconnect();
        }
      },
      { rootMargin: '240px' },
    );
    const visibility = new IntersectionObserver(([entry]) =>
      setVisible(entry.isIntersecting),
    );
    preload.observe(node);
    visibility.observe(node);
    return () => {
      preload.disconnect();
      visibility.disconnect();
    };
  }, [eager]);

  useEffect(() => {
    if (!mounted || !asset || !stage.current) return;
    setStatus('loading');
    const timeout = window.setTimeout(() => setStatus('error'), 25000);
    const engine = new SpineEngine(stage.current, {
      onReady: () => {
        clearTimeout(timeout);
        setStatus('ready');
        latest.current.onReady?.();
        engine.setAnimation(latest.current.animation);
        engine.setActive(
          latest.current.activePlayback && !document.hidden,
        );
      },
      onError: () => {
        clearTimeout(timeout);
        setStatus('error');
      },
      onAnimations: (animations) => {
        latest.current.onAnimations?.(
          animations.filter(
            (name): name is string => typeof name === 'string' && name.length > 0,
          ),
        );
      },
    });
    engineRef.current = engine;
    void engine.start({
      asset,
      animation: latest.current.animation,
      active: latest.current.activePlayback && !document.hidden,
      cdnBase: catalogCdnBaseUrl,
    });
    return () => {
      clearTimeout(timeout);
      engine.destroy();
      if (engineRef.current === engine) engineRef.current = null;
    };
    // Remount only when serialized asset / retry changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [mounted, attempt, asset ? JSON.stringify(asset) : 'static']);

  useEffect(() => {
    engineRef.current?.setAnimation(animation);
  }, [animation]);

  useEffect(() => {
    const sync = () =>
      engineRef.current?.setActive(activePlayback && !document.hidden);
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, [activePlayback]);

  return (
    <div
      ref={host}
      className={`${className} hero-spine-host`}
      aria-label={title}
      data-player-status={asset ? status : 'static'}
    >
      {(!asset || status !== 'ready') && (
        <div
          className={`hero-spine-placeholder${hasPrevious ? ' hero-spine-switch-status' : ''}`}
        >
          {!asset && Boolean(fallback) && <img src={fallback} alt={title} />}
          {asset && (
            <span role="status">
              {status === 'error'
                ? hasPrevious
                  ? '新动画加载失败，已保留上一形态'
                  : '动画暂时无法加载'
                : mounted
                  ? hasPrevious
                    ? '正在切换…'
                    : '动画加载中…'
                  : '滑动至此处加载动画'}
            </span>
          )}
          {asset && status === 'error' && (
            <button
              type="button"
              onClick={() => {
                setStatus('loading');
                setAttempt((value) => value + 1);
              }}
            >
              重试动画
            </button>
          )}
        </div>
      )}
      {/* Gesture hit-testing stays on this surface above the canvas. */}
      {asset && (
        <div className="hero-spine-gesture-surface" aria-hidden="true" />
      )}
      {mounted && asset && (
        <div
          ref={stage}
          className="hero-spine-stage"
          style={{
            position: 'absolute',
            inset: 0,
            opacity: status === 'ready' ? 1 : 0,
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
}
